/**
 * Demo cancel mutation (S3) — mirrors `useCancelBooking`'s shape and, per
 * PR #1375 S1, its full invalidation set: the bookings list, the detail
 * entry, and every bay-availability cache for the club — a cancelled demo
 * frees its bay, and a stale grid would keep showing it occupied.
 *
 * @module booking/hooks/useCancelDemoBooking
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { demoBookingService } from '../api/demoBookingService';

export interface UseCancelDemoBookingOptions {
  /** Required for cache invalidation — both call sites hold it. */
  clubId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useCancelDemoBooking(options: UseCancelDemoBookingOptions) {
  const queryClient = useQueryClient();
  const { clubId, onSuccess, onError } = options;

  return useMutation({
    mutationFn: async (bookingId: number) => {
      const result = await demoBookingService.cancelDemoBooking(bookingId);

      if (!result.success) {
        throw new Error(result.message || 'Failed to cancel your demo');
      }

      return result.payload;
    },

    onSuccess: (payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(clubId) });
      // detail() is not club-scoped (booking IDs are globally unique).
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(payload.bookingId) });
      // A slot opened up — refresh every bay-availability cache for the club.
      queryClient.invalidateQueries({
        queryKey: queryKeys.bays.all(clubId),
        refetchType: 'active',
      });
      onSuccess?.();
    },

    onError: (error: Error) => {
      console.error('[useCancelDemoBooking] Error:', error);
      onError?.(error);
    },
  });
}
